import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import {
  change,
  Field,
  formValueSelector,
  hasSubmitFailed,
  isSubmitting,
} from 'redux-form';
import DynamicForm from './DynamicForm';
import {
  clearPaymentMethods,
  getPaymentMethodsPerSecurityProfile,
} from '../../../actions';
import Select from './select/Select';
import {
  isRequired,
  validateMinNumber,
  validateNumber,
} from '../../../utils/validators';
import TextInput from './input/TextInput';
import {
  MAX_PAYMENT_METHODS,
  TRANSACTION_TYPE_CREDIT_CARD,
  TRANSACTION_TYPE_CREDIT_NOTE,
  TRANSACTION_TYPE_OTHER,
  TRANSACTION_TYPE_BANK_TRANSFER,
  TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL,
  TRANSACTION_TYPE_INTERNET,
  PAYMENT_METHODS,
} from '../../../config/constants';
import CreditNoteSelect from './select/CreditNoteSelect';
import FreeOfChargeCategorySelect from './select/FreeOfChargeCategorySelect';
import {
  clearFreeOfChargeCategory,
  getFreeOfChargeCategory,
} from '../../../actions/sales/FreeOfChargeCategory';
import BankAccountSelect from './select/BankAccountSelect';
import DatePicker from './input/DatePicker';
import { numberFormatter } from '../../../utils/number';
import { summarizeSplit } from '../../../utils/units/paymentSplit';
import PinpadPaymentButton from './PinpadPaymentButton';
import {
  postTotalPosSale,
  postTotalPosVoid,
} from '../../../actions/totalpos/Totalpos';
import printTotalPosVoid from '../../../utils/printers/TotalPosVoid';
import {
  recordPinpadEvent,
  startPinpadSale,
} from '../../../utils/pinpadBlackBox';
import PinpadActivityLog from './PinpadActivityLog';

const EXCLUSIVE_TRANSACTION_CODES = [
  TRANSACTION_TYPE_OTHER,
  TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL,
];

const PINPAD_TRANSACTION_CODES = [
  TRANSACTION_TYPE_CREDIT_CARD,
  TRANSACTION_TYPE_INTERNET,
];

const validateMin = validateMinNumber(1);

const HiddenField = () => null;

const PINPAD_LOCK_FIELD = 'pinpadLocked';

const validatePinpadIdle = (locked) =>
  locked ? 'Esperando respuesta del POS' : undefined;

const PinpadLockNotice = ({ input }) =>
  input.value ? (
    <div className="text-info mt-2">
      <i className="fa fa-spinner fa-spin" /> Esperando respuesta del POS...
    </div>
  ) : null;

PinpadLockNotice.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  }).isRequired,
};

export const isPinpadLocked = (formName) => (state) =>
  Boolean(formValueSelector(formName)(state, PINPAD_LOCK_FIELD));

const PINPAD_PENDING_SALES_FIELD = 'pinpadPendingSales';

const validateNoPendingSales = (count) =>
  count > 0 ? 'Anule los cobros POS pendientes' : undefined;

const PinpadPendingSalesNotice = ({ input }) =>
  input.value > 0 ? (
    <div className="alert alert-warning mt-2">
      <i className="fa fa-exclamation-triangle" /> La encomienda tiene{' '}
      {input.value + 1} cobros POS pendientes. Anule el cobro actual con
      &quot;Cancelar pago&quot; y presione &quot;Volver a iniciar pago&quot;
      para cargar el siguiente. Podrá entregar cuando quede uno solo.
    </div>
  ) : null;

PinpadPendingSalesNotice.propTypes = {
  input: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
};

export const hasPendingPinpadSales = (formName) => (state) =>
  formValueSelector(formName)(state, PINPAD_PENDING_SALES_FIELD) > 0;

export const getRowTransactionCode = (rowValues) =>
  rowValues && rowValues.paymentMethod
    ? rowValues.paymentMethod.transactionCode
    : null;

// amount only while charging or charged, to skip typing noise
const describeRowChanges = (previousRows, currentRows, pinpadStatus) => {
  if (previousRows.length !== currentRows.length) return [];

  const changes = [];
  currentRows.forEach((row, index) => {
    const previous = previousRows[index] || {};
    const current = row || {};
    const parts = [];

    if ((previous.voucherCode || null) !== (current.voucherCode || null)) {
      parts.push(
        `ref ${previous.voucherCode || '-'} -> ${current.voucherCode || '-'}`,
      );
    }
    if (Boolean(previous.pinpadPaid) !== Boolean(current.pinpadPaid)) {
      parts.push(
        `paid ${Boolean(previous.pinpadPaid)} -> ${Boolean(
          current.pinpadPaid,
        )}`,
      );
    }
    const watchAmount =
      previous.pinpadPaid ||
      current.pinpadPaid ||
      pinpadStatus[index] === 'processing';
    if (watchAmount && String(previous.amount) !== String(current.amount)) {
      parts.push(`amount ${previous.amount} -> ${current.amount}`);
    }

    if (parts.length) changes.push(`row=${index} ${parts.join(', ')}`);
  });
  return changes;
};

const generatePaymentSplitColumns = ({
  paymentMethodOptions,
  paymentMethodLoading,
  onMethodChange,
  selectedMethodIds,
  showVoucherField = false,
  onCreditNoteChange,
  allowExclusiveMethods,
  freeOfChargeSubCategories,
  freeOfChargeUsers,
  onFreeOfChargeCategoryChange,
  onFreeOfChargeSubCategoryChange,
  pinpadStatus,
  onStartPinpadPayment,
  onCancelPinpadPayment,
  onPrintVoidVoucher,
  onRestartPinpadPayment,
  isPinpadPaid,
  pinpadProcessing,
  totalPosLoading,
}) => [
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Método de pago',
    name: 'paymentMethod',
    component: Select,
    validate: [isRequired],
    onChange: onMethodChange,
    otherProps: {
      isDisabled: isPinpadPaid,
      isClearable: false,
      options: (rowValues) =>
        paymentMethodOptions.filter((option) => {
          if (
            EXCLUSIVE_TRANSACTION_CODES.includes(option.transactionCode) &&
            !allowExclusiveMethods
          ) {
            return false;
          }
          return (
            (rowValues &&
              rowValues.paymentMethod &&
              rowValues.paymentMethod.value === option.value) ||
            !selectedMethodIds.includes(option.value)
          );
        }),
      isLoading: paymentMethodLoading,
    },
  },
  {
    lg: showVoucherField ? 3 : 6,
    md: showVoucherField ? 3 : 6,
    sm: 12,
    label: 'Monto',
    requiredLabel: true,
    name: 'amount',
    component: TextInput,
    type: 'number',
    placeholder: '0.00',
    validate: [isRequired, validateNumber, validateMin],
    hidden: (rowValues) => {
      const code = getRowTransactionCode(rowValues);
      return (
        code === TRANSACTION_TYPE_CREDIT_NOTE || code === TRANSACTION_TYPE_OTHER
      );
    },
    disabled: (rowValues) => pinpadProcessing || isPinpadPaid(rowValues),
  },
  {
    lg: 2,
    md: 2,
    sm: 12,
    label: 'Nº Referencia (POS)',
    name: 'voucherCode',
    requiredLabel: true,
    component: TextInput,
    type: 'text',
    placeholder: 'Código de Voucher',
    validate: [isRequired],
    disabled: isPinpadPaid,
    hidden: (rowValues) =>
      !PINPAD_TRANSACTION_CODES.includes(getRowTransactionCode(rowValues)),
  },
  {
    lg: 2,
    md: 2,
    sm: 12,
    customizedField: 'custom',
    hidden: (rowValues) =>
      !PINPAD_TRANSACTION_CODES.includes(getRowTransactionCode(rowValues)),
    render: (index) => (
      <PinpadPaymentButton
        index={index}
        status={pinpadStatus[index]}
        onStart={onStartPinpadPayment}
        onCancel={onCancelPinpadPayment}
        onPrintVoid={onPrintVoidVoucher}
        onRestart={onRestartPinpadPayment}
        totalPosLoading={totalPosLoading}
      />
    ),
  },
  {
    lg: 6,
    md: 6,
    sm: 12,
    label: 'Notas de crédito',
    name: 'creditNotes',
    component: CreditNoteSelect,
    validate: [isRequired],
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_CREDIT_NOTE,
    onChange: onCreditNoteChange,
    otherProps: {
      isMulti: true,
      isCustomerIdRequired: true,
      optionsRegistered: true,
    },
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Categoría de Servicio a título gratuito',
    name: 'freeOfChargeCategoryId',
    component: FreeOfChargeCategorySelect,
    requiredLabel: true,
    validate: [isRequired],
    onChange: onFreeOfChargeCategoryChange,
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_OTHER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'SubCategoría',
    name: 'freeOfChargeSubCategory',
    component: Select,
    requiredLabel: true,
    validate: [isRequired],
    onChange: onFreeOfChargeSubCategoryChange,
    otherProps: { options: freeOfChargeSubCategories },
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_OTHER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Autorizador',
    name: 'freeOfChargeUser',
    component: Select,
    requiredLabel: true,
    validate: [isRequired],
    otherProps: { options: freeOfChargeUsers },
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_OTHER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Cuenta Bancaria',
    name: 'bankAccount',
    component: BankAccountSelect,
    requiredLabel: true,
    validate: [isRequired],
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_BANK_TRANSFER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Número de Operación',
    name: 'voucherCode',
    component: TextInput,
    type: 'text',
    placeholder: 'Número de operación',
    requiredLabel: true,
    validate: [isRequired],
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_BANK_TRANSFER,
  },
  {
    lg: 4,
    md: 4,
    sm: 12,
    label: 'Fecha de Depósito',
    name: 'depositDate',
    component: DatePicker,
    placeholder: 'Fecha de depósito',
    requiredLabel: true,
    validate: [isRequired],
    hidden: (rowValues) =>
      getRowTransactionCode(rowValues) !== TRANSACTION_TYPE_BANK_TRANSFER,
  },
];

const MultiPaymentMethodInputGroup = ({
  name,
  formName,
  dispatchGetPaymentMethods,
  dispatchClearPaymentMethods,
  paymentMethodOptions,
  paymentMethodLoading,
  dispatchChange,
  transactionListValues,
  maxPaymentMethods,
  totalPrice,
  defaultPaymentMethod,
  dispatchClearFreeOfChargeCategory,
  dispatchGetFreeOfChargeCategory,
  freeOfChargeSubCategories,
  freeOfChargeRawSubCategories,
  recomendedPaymentMethod,
  showSuggestion,
  dispatchPostTotalPosSale,
  dispatchPostTotalPosVoid,
  idUserName,
  totalPosLoading,
  formSubmitting,
  formSubmitFailed,
  onPinpadTransactionApproved,
  pendingPinpadSales,
  onPendingSaleRestored,
}) => {
  const [selectedMethodIds, setSelectedMethodIds] = useState([]);
  const [showVoucherField, setShowVoucherField] = useState(false);
  const [quantity, setQuantity] = useState(0);
  const [hasExclusiveMethod, setHasExclusiveMethod] = useState(false);
  const [freeOfChargeUsers, setFreeOfChargeUsers] = useState([]);

  const [pinpadStatus, setPinpadStatus] = useState({});

  const pinpadTxLogRef = useRef([]);

  // latest rows, readable from async handlers and unmount cleanup
  const rowsRef = useRef(transactionListValues);
  rowsRef.current = transactionListValues;
  const previousRowsRef = useRef(transactionListValues);
  const previousSubmitRef = useRef({
    submitting: formSubmitting,
    failed: formSubmitFailed,
  });

  const saleIdRef = useRef(null);
  const [saleId, setSaleId] = useState(null);

  const logEvent = (type, detail, meta) =>
    recordPinpadEvent({
      saleId: saleIdRef.current,
      form: formName,
      type,
      detail,
      rows: rowsRef.current,
      meta,
    });

  const appendPinpadTransaction = (entry) => {
    pinpadTxLogRef.current = [
      ...pinpadTxLogRef.current,
      { clientTime: new Date().toISOString(), ...entry },
    ];
    dispatchChange(formName, 'pinPadTransactions', pinpadTxLogRef.current);
  };

  const persistPinpadTransaction = async (entry) => {
    if (!onPinpadTransactionApproved) return null;
    let persistedId = null;
    try {
      persistedId = await onPinpadTransactionApproved(entry);
    } catch {
      persistedId = null;
    }
    logEvent(
      persistedId ? 'PINPAD_PERSISTED' : 'PINPAD_PERSIST_FAILED',
      `row=${entry.index} operation=${entry.operation} id=${
        persistedId || '-'
      }`,
    );
    return persistedId;
  };

  const setPinpadLock = (locked) =>
    dispatchChange(formName, PINPAD_LOCK_FIELD, locked);

  const pendingSalesRef = useRef([]);
  const restoredSaleRef = useRef(null);

  // a card sale needs the card option, which may not be loaded yet
  const resolveRestoredPaymentMethod = (isQrPayment) =>
    isQrPayment
      ? PAYMENT_METHODS.QR
      : paymentMethodOptions.find(
          (option) => option.transactionCode === TRANSACTION_TYPE_CREDIT_CARD,
        ) || null;

  // an already charged sale: restore it in the row as paid instead of
  // charging again; the next queued one loads once this one is voided
  const restoreNextPendingSale = () => {
    const [sale, ...rest] = pendingSalesRef.current;
    pendingSalesRef.current = rest;
    restoredSaleRef.current = sale;
    dispatchChange(formName, PINPAD_PENDING_SALES_FIELD, rest.length);

    const { transaction, amount, isQrPayment } = sale;
    const paymentMethod = resolveRestoredPaymentMethod(isQrPayment);
    logEvent(
      'RESTORE_SALE',
      `id=${transaction.id} ref=${transaction.financialReference} amount=${amount} queued=${rest.length}`,
    );

    appendPinpadTransaction(transaction);
    dispatchChange(formName, name, [
      {
        amount,
        paymentMethod,
        isQrPayment,
        voucherCode: transaction.financialReference,
        pinpadPaid: true,
        pinpadTransactionId: transaction.id,
      },
    ]);
    setPinpadStatus({ 0: 'done' });
    setShowVoucherField(true);
    setSelectedMethodIds(paymentMethod ? [paymentMethod.value] : []);

    if (onPendingSaleRestored) onPendingSaleRestored(sale);
  };

  useLayoutEffect(() => {
    // MOUNT logs the rows the reset below is about to wipe
    saleIdRef.current = startPinpadSale({
      form: formName,
      total: totalPrice,
      userId: idUserName,
    });
    setSaleId(saleIdRef.current);
    logEvent('MOUNT');
    pinpadTxLogRef.current = [];
    dispatchChange(formName, 'pinPadTransactions', []);
    setPinpadLock(false);
    pendingSalesRef.current = pendingPinpadSales;
    dispatchChange(formName, PINPAD_PENDING_SALES_FIELD, 0);

    const isEmptyRecomended =
      !recomendedPaymentMethod || recomendedPaymentMethod.length === 0;

    if (isEmptyRecomended) {
      dispatchGetPaymentMethods();
    }

    setQuantity(1);

    if (pendingPinpadSales.length) {
      restoreNextPendingSale();
    } else {
      dispatchChange(formName, name, [
        {
          amount: totalPrice,
          paymentMethod: defaultPaymentMethod,
        },
      ]);
      if (
        defaultPaymentMethod &&
        PINPAD_TRANSACTION_CODES.includes(defaultPaymentMethod.transactionCode)
      ) {
        setShowVoucherField(true);
      }
      if (defaultPaymentMethod && defaultPaymentMethod.value) {
        setSelectedMethodIds([defaultPaymentMethod.value]);
      }
    }

    return () => {
      logEvent('UNMOUNT');
      if (isEmptyRecomended) {
        dispatchClearPaymentMethods();
      }
    };
  }, []);

  useEffect(() => {
    describeRowChanges(
      previousRowsRef.current,
      transactionListValues,
      pinpadStatus,
    ).forEach((rowChange) => logEvent('ROW_CHANGE', rowChange));
    previousRowsRef.current = transactionListValues;
  }, [transactionListValues]);

  useEffect(() => {
    const previous = previousSubmitRef.current;
    if (formSubmitting !== previous.submitting) {
      logEvent(formSubmitting ? 'SUBMIT_START' : 'SUBMIT_END');
    }
    if (formSubmitFailed && !previous.failed) {
      logEvent('SUBMIT_FAILED');
    }
    previousSubmitRef.current = {
      submitting: formSubmitting,
      failed: formSubmitFailed,
    };
  }, [formSubmitting, formSubmitFailed]);

  useEffect(() => {
    if (
      quantity === 1 &&
      transactionListValues.length === 1 &&
      (!transactionListValues[0] ||
        (!transactionListValues[0].paymentMethod &&
          !transactionListValues[0].pinpadPaid))
    ) {
      dispatchChange(formName, `${name}[0].amount`, totalPrice);
    }
  }, [totalPrice]);

  useEffect(() => {
    const sale = restoredSaleRef.current;
    const row = transactionListValues[0];
    if (!sale || sale.isQrPayment || !row || !row.pinpadPaid) return;
    if (row.paymentMethod) return;

    const cardOption = resolveRestoredPaymentMethod(false);
    if (!cardOption) return;

    dispatchChange(formName, `${name}[0].paymentMethod`, cardOption);
    setSelectedMethodIds([cardOption.value]);
  }, [paymentMethodOptions, transactionListValues.length]);

  useEffect(() => {
    if (!hasExclusiveMethod || freeOfChargeRawSubCategories.length !== 1)
      return;

    const focIndex = transactionListValues.findIndex(
      (t) =>
        t.paymentMethod &&
        t.paymentMethod.transactionCode === TRANSACTION_TYPE_OTHER,
    );
    if (focIndex === -1) return;
    if (transactionListValues[focIndex].freeOfChargeSubCategory) return;

    const only = freeOfChargeRawSubCategories[0];
    dispatchChange(formName, `${name}[${focIndex}].freeOfChargeSubCategory`, {
      value: only.id,
      label: only.name,
    });

    const users = (only.freeOfChargeSubCategoryUserList || []).map((u) => ({
      value: u.customer.id,
      label: u.customer.fullName,
    }));
    setFreeOfChargeUsers(users);

    if (users.length === 1) {
      dispatchChange(
        formName,
        `${name}[${focIndex}].freeOfChargeUser`,
        users[0],
      );
    }
  }, [freeOfChargeRawSubCategories]);

  const onMethodChange = (index, option) => {
    const previousRow = transactionListValues[index];
    const previousId =
      previousRow && previousRow.paymentMethod
        ? previousRow.paymentMethod.value
        : null;

    setSelectedMethodIds((prevIds) => {
      const withoutPrevious = prevIds.filter((id) => id !== previousId);
      return option ? [...withoutPrevious, option.value] : withoutPrevious;
    });

    const hasCardElsewhere = transactionListValues.some(
      (transaction, i) =>
        i !== index &&
        transaction.paymentMethod &&
        PINPAD_TRANSACTION_CODES.includes(
          transaction.paymentMethod.transactionCode,
        ),
    );
    setShowVoucherField(
      hasCardElsewhere ||
        Boolean(
          option && PINPAD_TRANSACTION_CODES.includes(option.transactionCode),
        ),
    );

    const previousCode =
      previousRow && previousRow.paymentMethod
        ? previousRow.paymentMethod.transactionCode
        : null;
    const newCode = option ? option.transactionCode : null;

    logEvent(
      'METHOD',
      `row=${index} ${previousCode || '-'} -> ${newCode || '-'}${
        pinpadStatus[index] ? ` status=${pinpadStatus[index]}` : ''
      }`,
    );

    if (previousCode !== newCode) {
      if (PINPAD_TRANSACTION_CODES.includes(previousCode)) {
        dispatchChange(formName, `${name}[${index}].voucherCode`, null);
        dispatchChange(formName, `${name}[${index}].isQrPayment`, null);
        setPinpadStatus((prev) => {
          const n = { ...prev };
          delete n[index];
          return n;
        });
      }
      if (previousCode === TRANSACTION_TYPE_BANK_TRANSFER) {
        dispatchChange(formName, `${name}[${index}].voucherCode`, null);
        dispatchChange(formName, `${name}[${index}].bankAccount`, null);
        dispatchChange(formName, `${name}[${index}].depositDate`, null);
      }
      if (previousCode === TRANSACTION_TYPE_CREDIT_NOTE) {
        dispatchChange(formName, `${name}[${index}].creditNotes`, null);
        dispatchChange(formName, `${name}[${index}].amount`, null);
      }

      if (previousCode === TRANSACTION_TYPE_OTHER) {
        dispatchChange(
          formName,
          `${name}[${index}].freeOfChargeCategoryId`,
          null,
        );
        dispatchChange(
          formName,
          `${name}[${index}].freeOfChargeSubCategory`,
          null,
        );
        dispatchChange(formName, `${name}[${index}].freeOfChargeUser`, null);
        dispatchClearFreeOfChargeCategory();
        setFreeOfChargeUsers([]);
      }
      if (newCode === TRANSACTION_TYPE_OTHER) {
        dispatchChange(formName, `${name}[${index}].amount`, totalPrice);
      }

      if (newCode === TRANSACTION_TYPE_PAYMENT_ON_ARRIVAL) {
        dispatchChange(formName, `${name}[${index}].amount`, totalPrice);
      }
    }

    setHasExclusiveMethod(EXCLUSIVE_TRANSACTION_CODES.includes(newCode));
  };

  const onStartPinpadPayment = async (index) => {
    const row = transactionListValues[index];
    const amount = parseFloat(row && row.amount);
    const startAsQr = getRowTransactionCode(row) === TRANSACTION_TYPE_INTERNET;

    if (!amount || amount <= 0) {
      toastr.error('Error', 'Ingrese un monto válido antes de cobrar');
      return;
    }

    setPinpadLock(true);
    setPinpadStatus((prev) => ({ ...prev, [index]: 'processing' }));
    logEvent('SALE_START', `row=${index} amount=${amount} qr=${startAsQr}`);

    try {
      const response = await dispatchPostTotalPosSale({
        amount,
        operator: idUserName,
        qrPayment: startAsQr,
        blackBoxSaleId: saleIdRef.current,
      });

      logEvent(
        response ? 'SALE_RESPONSE' : 'SALE_NO_RESPONSE',
        response
          ? `row=${index} approved=${Boolean(response.approved)} ref=${
              response.financialReference
            } method=${response.paymentMethod}`
          : `row=${index}`,
        response
          ? {
              approved: Boolean(response.approved),
              reference: response.financialReference,
            }
          : null,
      );

      const saleEntry = {
        operation: 'SALE',
        index,
        approved: Boolean(response && response.approved),
        responseCode: response ? response.responseCode : null,
        financialReference: response ? response.financialReference : null,
        response: response || null,
        clientTime: new Date().toISOString(),
      };

      // only approved sales are persisted; rejected attempts go on submit
      const persistedId = saleEntry.approved
        ? await persistPinpadTransaction(saleEntry)
        : null;

      appendPinpadTransaction(
        persistedId ? { ...saleEntry, id: persistedId } : saleEntry,
      );

      if (response && response.approved) {
        // the row shows what the POS charged, even if it changed meanwhile
        const chargedAmount = parseFloat(response.amount);
        if (!Number.isNaN(chargedAmount)) {
          const currentRow = rowsRef.current[index];
          const currentAmount = parseFloat(currentRow && currentRow.amount);
          if (Math.abs(chargedAmount - currentAmount) > 0.005) {
            logEvent(
              'AMOUNT_MISMATCH',
              `row=${index} rowAmount=${currentAmount} charged=${chargedAmount}`,
            );
            toastr.warning(
              'Advertencia',
              `El POS cobró ${numberFormatter({
                style: 'currency',
                value: chargedAmount,
              })}. Complete la diferencia con otro método de pago o anule el cobro.`,
            );
          }
          dispatchChange(formName, `${name}[${index}].amount`, chargedAmount);
        }
        dispatchChange(
          formName,
          `${name}[${index}].pinpadTransactionId`,
          persistedId,
        );
        if (response.paymentMethod === 'QR' && !startAsQr) {
          onMethodChange(index, PAYMENT_METHODS.QR);
          dispatchChange(
            formName,
            `${name}[${index}].paymentMethod`,
            PAYMENT_METHODS.QR,
          );
        }

        dispatchChange(
          formName,
          `${name}[${index}].isQrPayment`,
          response.paymentMethod === 'QR',
        );
        dispatchChange(
          formName,
          `${name}[${index}].voucherCode`,
          response.financialReference,
        );
        dispatchChange(formName, `${name}[${index}].pinpadPaid`, true);

        dispatchChange(formName, `${name}[${index}].pinpadResponse`, response);

        setPinpadStatus((prev) => ({ ...prev, [index]: 'done' }));
      } else {
        setPinpadStatus((prev) => {
          const next = { ...prev };
          delete next[index];
          return next;
        });
      }
    } catch (err) {
      logEvent('SALE_ERROR', `row=${index} ${err ? err.message : ''}`);
      toastr.error('Error', 'No se pudo comunicar con el POS');
      appendPinpadTransaction({
        operation: 'SALE',
        index,
        approved: false,
        responseCode: 'COMM_ERROR',
        financialReference: null,
        response: null,
      });
      setPinpadStatus((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    } finally {
      setPinpadLock(false);
    }
  };

  const onCancelPinpadPayment = async (index) => {
    const row = transactionListValues[index];
    logEvent(
      'VOID_START',
      `row=${index} ref=${row.voucherCode} amount=${row.amount} qr=${Boolean(
        row.isQrPayment,
      )}`,
    );
    setPinpadLock(true);
    try {
      const response = await dispatchPostTotalPosVoid({
        financialReference: row.voucherCode,
        operator: idUserName,
        amount: parseFloat(row.amount),
        qrPayment: Boolean(row.isQrPayment),
        blackBoxSaleId: saleIdRef.current,
      });

      logEvent(
        response ? 'VOID_RESPONSE' : 'VOID_NOT_DONE',
        response
          ? `row=${index} approved=${Boolean(response.approved)}`
          : `row=${index}`,
        response ? { approved: Boolean(response.approved) } : null,
      );

      if (!response) {
        return;
      }

      const voidEntry = {
        operation: 'VOID',
        index,
        approved: Boolean(response.approved),
        responseCode: response.responseCode,
        financialReference: row.voucherCode,
        response,
        clientTime: new Date().toISOString(),
      };

      // a void is persisted only against a persisted sale
      const persistedId =
        voidEntry.approved && row.pinpadTransactionId
          ? await persistPinpadTransaction({
              ...voidEntry,
              originalTransactionId: row.pinpadTransactionId,
            })
          : null;

      appendPinpadTransaction(
        persistedId
          ? {
              ...voidEntry,
              id: persistedId,
              originalTransactionId: row.pinpadTransactionId,
            }
          : voidEntry,
      );

      dispatchChange(formName, `${name}[${index}].pinpadTransactionId`, null);
      dispatchChange(formName, `${name}[${index}].pinpadPaid`, null);
      dispatchChange(formName, `${name}[${index}].voucherCode`, null);
      dispatchChange(formName, `${name}[${index}].pinpadResponse`, null);
      dispatchChange(formName, `${name}[${index}].voidResponse`, response);
      setPinpadStatus((prev) => ({ ...prev, [index]: 'voided' }));
    } finally {
      setPinpadLock(false);
    }
  };

  const onPrintVoidVoucher = (index) => {
    const row = transactionListValues[index];
    if (row && row.voidResponse) {
      printTotalPosVoid(row.voidResponse, row.isQrPayment);
    }
  };

  const onRestartPinpadPayment = (index) => {
    logEvent('RESTART', `row=${index}`);
    if (index === 0 && pendingSalesRef.current.length) {
      restoreNextPendingSale();
      return;
    }
    setPinpadStatus((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const reindexAfterRemove = (map, removedIndex) => {
    const next = {};
    Object.keys(map).forEach((key) => {
      const i = Number(key);
      if (i === removedIndex) return;
      next[i > removedIndex ? i - 1 : i] = map[key];
    });
    return next;
  };

  const onClickRemove = (index, fields) => {
    const removedMethod = transactionListValues[index];
    logEvent('REMOVE_ROW', `row=${index}`);

    let newSelectedIds = [...selectedMethodIds];

    if (removedMethod && removedMethod.paymentMethod) {
      const removedId = removedMethod.paymentMethod.value;
      newSelectedIds = newSelectedIds.filter((id) => id !== removedId);
      setSelectedMethodIds(newSelectedIds);
    }

    const hasCard = transactionListValues.some(
      (transaction, i) =>
        i !== index &&
        transaction.paymentMethod &&
        PINPAD_TRANSACTION_CODES.includes(
          transaction.paymentMethod.transactionCode,
        ),
    );

    setShowVoucherField(hasCard);

    if (
      removedMethod &&
      removedMethod.paymentMethod &&
      EXCLUSIVE_TRANSACTION_CODES.includes(
        removedMethod.paymentMethod.transactionCode,
      )
    ) {
      setHasExclusiveMethod(false);
      dispatchClearFreeOfChargeCategory();
      setFreeOfChargeUsers([]);
    }

    fields.remove(index);
    setQuantity((q) => q - 1);
    setPinpadStatus((prev) => reindexAfterRemove(prev, index));
  };

  const onClickAdd = (fields) => {
    const remainingAmount =
      totalPrice -
      transactionListValues.reduce(
        (sum, transaction) => sum + (parseFloat(transaction.amount) || 0),
        0,
      );
    logEvent('ADD_ROW');
    fields.push({
      amount: remainingAmount > 0 ? remainingAmount : null,
    });
    setQuantity((q) => q + 1);
  };

  const onCreditNoteChange = (index, option) => {
    const selectedCreditNotes = Object.values(option || {}).filter(
      (opt) => opt && typeof opt === 'object' && 'value' in opt,
    );

    const total = selectedCreditNotes.reduce(
      (sum, note) => sum + (parseFloat(note.amount) || 0),
      0,
    );

    dispatchChange(
      formName,
      `${name}[${index}].amount`,
      total > 0 ? total.toFixed(2) : null,
    );
  };

  const onFreeOfChargeCategoryChange = (index, option) => {
    dispatchChange(formName, `${name}[${index}].freeOfChargeSubCategory`, null);
    dispatchChange(formName, `${name}[${index}].freeOfChargeUser`, null);
    setFreeOfChargeUsers([]);
    if (option && option.value) {
      dispatchGetFreeOfChargeCategory({ freeOfChargeCategoryId: option.value });
    }
  };

  const onFreeOfChargeSubCategoryChange = (index, option) => {
    dispatchChange(formName, `${name}[${index}].freeOfChargeUser`, null);

    const found =
      option && option.value
        ? freeOfChargeRawSubCategories.find((sc) => sc.id === option.value)
        : null;

    setFreeOfChargeUsers(
      found
        ? (found.freeOfChargeSubCategoryUserList || []).map((u) => ({
            value: u.customer.id,
            label: u.customer.fullName,
          }))
        : [],
    );
  };

  const isPinpadPaid = (rowValues) =>
    Boolean(rowValues && rowValues.pinpadPaid);

  const {
    assigned: assignedAmount,
    remaining: remainingAmount,
    isPending,
    isExceeded,
    isOverpaidByCredit: isCoveredByCreditNote,
  } = summarizeSplit(transactionListValues, totalPrice);

  let statusClass = 'PaymentSplitCard--ok';
  if (isPending) statusClass = 'PaymentSplitCard--warning';
  if (isExceeded) statusClass = 'PaymentSplitCard--danger';

  return (
    <>
      <DynamicForm
        name={name}
        columns={generatePaymentSplitColumns({
          paymentMethodOptions,
          onMethodChange,
          selectedMethodIds,
          paymentMethodLoading,
          showVoucherField,
          onCreditNoteChange,
          allowExclusiveMethods: quantity === 1,
          freeOfChargeSubCategories,
          freeOfChargeUsers,
          onFreeOfChargeCategoryChange,
          onFreeOfChargeSubCategoryChange,
          pinpadStatus,
          onStartPinpadPayment,
          onCancelPinpadPayment,
          onPrintVoidVoucher,
          onRestartPinpadPayment,
          isPinpadPaid,
          pinpadProcessing: Object.values(pinpadStatus).includes('processing'),
          totalPosLoading,
        })}
        labelWithoutElements="Ningún método de pago seleccionado"
        onClickRemove={onClickRemove}
        onClickAdd={onClickAdd}
        showAddButton={
          quantity < maxPaymentMethods && !hasExclusiveMethod && isPending
        }
        showRemoveButton={(rowValues) =>
          quantity > 1 &&
          !hasExclusiveMethod &&
          !(rowValues && rowValues.pinpadPaid) &&
          !Object.values(pinpadStatus).includes('processing')
        }
      />
      <Field name="pinPadTransactions" component={HiddenField} />
      <Field
        name={PINPAD_PENDING_SALES_FIELD}
        component={PinpadPendingSalesNotice}
        validate={validateNoPendingSales}
      />
      <Field
        name={PINPAD_LOCK_FIELD}
        component={PinpadLockNotice}
        validate={validatePinpadIdle}
      />
      <PinpadActivityLog userId={idUserName} saleId={saleId} />
      {showSuggestion && (
        <div className="PaymentSplitSummary mt-2 mb-2">
          <div className="PaymentSplitCard">
            <span className="PaymentSplitCard__label">Total</span>
            <span className="PaymentSplitCard__value">
              {numberFormatter({ style: 'currency', value: totalPrice })}
            </span>
          </div>
          <div className="PaymentSplitCard">
            <span className="PaymentSplitCard__label">Asignado</span>
            <span className="PaymentSplitCard__value">
              {numberFormatter({ style: 'currency', value: assignedAmount })}
            </span>
          </div>
          <div className={`PaymentSplitCard ${statusClass}`}>
            <span className="PaymentSplitCard__label">
              {isExceeded || isCoveredByCreditNote ? 'Excedente' : 'Pendiente'}
            </span>
            <span className="PaymentSplitCard__value">
              {numberFormatter({
                style: 'currency',
                value: Math.abs(remainingAmount),
              })}
            </span>
            {isPending && (
              <span className="PaymentSplitCard__hint">
                La suma debe igualar el total
              </span>
            )}
            {isExceeded && (
              <span className="PaymentSplitCard__hint">
                Los montos superan el total
              </span>
            )}
            {isCoveredByCreditNote && (
              <span className="PaymentSplitCard__hint">
                Excedente cubierto por nota de crédito
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

MultiPaymentMethodInputGroup.propTypes = {
  name: PropTypes.string,
  dispatchGetPaymentMethods: PropTypes.func.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  paymentMethodOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
      transactionCode: PropTypes.string,
    }),
  ),
  paymentMethodLoading: PropTypes.bool,
  // eslint-disable-next-line
  transactionCodesNotIncluded: PropTypes.arrayOf(PropTypes.string),
  formName: PropTypes.string.isRequired,
  transactionListValues: PropTypes.arrayOf(
    PropTypes.shape({
      paymentMethod: PropTypes.shape({
        value: PropTypes.number,
        label: PropTypes.string,
        transactionCode: PropTypes.string,
      }),
      amount: PropTypes.number,
      freeOfChargeSubCategory: PropTypes.shape({
        value: PropTypes.number,
        label: PropTypes.string,
      }),
      freeOfChargeUser: PropTypes.shape({
        value: PropTypes.number,
        label: PropTypes.string,
      }),
      voucherCode: PropTypes.string,
      isQrPayment: PropTypes.bool,
      voidResponse: PropTypes.shape({
        approved: PropTypes.bool,
        legend: PropTypes.string,
      }),
      pinpadTransactionId: PropTypes.number,
      pinpadPaid: PropTypes.bool,
      pinpadResponse: PropTypes.shape({
        approved: PropTypes.bool,
        financialReference: PropTypes.string,
        paymentMethod: PropTypes.string,
        responseCode: PropTypes.string,
      }),
    }),
  ).isRequired,
  maxPaymentMethods: PropTypes.number,
  defaultPaymentMethod: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
    transactionCode: PropTypes.string,
  }),
  totalPrice: PropTypes.number,
  dispatchGetFreeOfChargeCategory: PropTypes.func.isRequired,
  dispatchClearFreeOfChargeCategory: PropTypes.func.isRequired,
  freeOfChargeSubCategories: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
    }),
  ),
  freeOfChargeRawSubCategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      freeOfChargeSubCategoryUserList: PropTypes.arrayOf(
        PropTypes.shape({
          customer: PropTypes.shape({
            id: PropTypes.number,
            fullName: PropTypes.string,
          }),
        }),
      ),
    }),
  ),
  recomendedPaymentMethod: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      label: PropTypes.string,
      transactionCode: PropTypes.string,
    }),
  ),
  showSuggestion: PropTypes.bool,
  dispatchClearPaymentMethods: PropTypes.func.isRequired,
  dispatchPostTotalPosSale: PropTypes.func.isRequired,
  dispatchPostTotalPosVoid: PropTypes.func.isRequired,
  idUserName: PropTypes.string.isRequired,
  totalPosLoading: PropTypes.bool.isRequired,
  formSubmitting: PropTypes.bool.isRequired,
  formSubmitFailed: PropTypes.bool.isRequired,
  // async (entry) => persisted id or null, called once per approved sale or
  // void; a void entry carries the persisted sale's originalTransactionId
  onPinpadTransactionApproved: PropTypes.func,
  // already charged sales to restore one at a time instead of charging again
  pendingPinpadSales: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number,
      isQrPayment: PropTypes.bool,
      transaction: PropTypes.shape({
        id: PropTypes.number,
        financialReference: PropTypes.string,
      }),
    }),
  ),
  onPendingSaleRestored: PropTypes.func,
};

MultiPaymentMethodInputGroup.defaultProps = {
  name: 'transactionList',
  transactionCodesNotIncluded: [],
  paymentMethodOptions: [],
  paymentMethodLoading: false,
  maxPaymentMethods: MAX_PAYMENT_METHODS,
  defaultPaymentMethod: undefined,
  totalPrice: 0,
  freeOfChargeSubCategories: [],
  freeOfChargeRawSubCategories: [],
  recomendedPaymentMethod: [],
  showSuggestion: true,
  onPinpadTransactionApproved: null,
  pendingPinpadSales: [],
  onPendingSaleRestored: null,
};

const mapStateToProps = (
  state,
  {
    transactionCodesNotIncluded = [],
    formName,
    name = 'transactionList',
    recomendedPaymentMethod = [],
  },
) => {
  const baseOptions =
    recomendedPaymentMethod.length > 0
      ? recomendedPaymentMethod
      : (
          state.AccountingUnit.PaymentMethod.getIn([
            'all',
            'content',
            'content',
          ]) || []
        ).map((paymentMethod) => ({
          value: paymentMethod.id,
          label: paymentMethod.name,
          transactionCode: paymentMethod.transactionCode,
        }));

  const filteredOptions = baseOptions.filter((paymentMethod) => {
    if (transactionCodesNotIncluded.length === 0) {
      return true;
    }
    return !transactionCodesNotIncluded.includes(paymentMethod.transactionCode);
  });

  const selector = formValueSelector(formName);

  const transactionListValues = selector(state, name) || [];

  const categoryState = state.SalesUnit.FreeOfChargeCategory.getIn([
    'current',
    'content',
  ]);
  const category =
    categoryState && categoryState.toJS ? categoryState.toJS() : categoryState;

  let freeOfChargeSubCategories = [];
  let freeOfChargeRawSubCategories = [];

  if (category) {
    const subcats = (category.freeOfChargeSubCategoryList || []).filter(
      (sc) => sc.active === true,
    );
    freeOfChargeRawSubCategories = subcats;
    freeOfChargeSubCategories = subcats.map((sc) => ({
      value: sc.id,
      label: sc.name,
    }));
  }

  return {
    paymentMethodOptions: filteredOptions,
    paymentMethodLoading: state.AccountingUnit.PaymentMethod.getIn([
      'all',
      'loading',
    ]),
    transactionListValues,
    freeOfChargeSubCategories,
    freeOfChargeRawSubCategories,
    idUserName: String(state.authentication.getIn(['user', 'id'])),
    totalPosLoading: !state.TotalPosUnit.TotalPos.getIn([
      'current',
      'activity',
    ]).isEmpty(),
    formSubmitting: isSubmitting(formName)(state),
    formSubmitFailed: hasSubmitFailed(formName)(state),
  };
};

const mapDispatchToProps = {
  dispatchGetPaymentMethods: getPaymentMethodsPerSecurityProfile,
  dispatchClearPaymentMethods: clearPaymentMethods,
  dispatchChange: change,
  dispatchGetFreeOfChargeCategory: getFreeOfChargeCategory,
  dispatchClearFreeOfChargeCategory: clearFreeOfChargeCategory,
  dispatchPostTotalPosSale: postTotalPosSale,
  dispatchPostTotalPosVoid: postTotalPosVoid,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MultiPaymentMethodInputGroup);
